import styles from './styles.module.scss';

import { useContext, useState } from 'react';
import {
    ListItem,
    ListItemButton,
    ListItemText,
    Checkbox,
    ListItemAvatar,
    Avatar,
    TextField
} from '@mui/material';
import { WindowSizeContext } from '../../providers/WindowSizeProvider';

interface MultipleSelectionListProps extends React.HTMLAttributes<HTMLDivElement> {
    data: any[];
    primaryValue: string;
    secondaryValue?: string;
    setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
    selectedItems: React.ComponentState;
}

export const MultipleSelectionList = ({
    data,
    primaryValue,
    secondaryValue,
    setSelectedItems,
    selectedItems
}: MultipleSelectionListProps) => {
    const windowSize = useContext(WindowSizeContext);
    const [search, setSearch] = useState<string>('');
    const [maxRender, setMaxRender] = useState<number>(20);
    const filteredItems = data.map(addSelectedAttribute).filter(filterArray).sort(sortArray);
    let itemsRendered: number = 0;

    function addSelectedAttribute(object: any) {
        object.isSelected = selectedItems.includes(object.id);
        return object;
    }

    function filterArray(object: any) {
        if (selectedItems.includes(object.id)) {
            return true;
        } else if (object[primaryValue].toLocaleLowerCase().includes(search.toLocaleLowerCase())) {
            return true;
        } else if (object[secondaryValue ?? '']) {
            if (
                object[secondaryValue ?? '']
                    .toLocaleLowerCase()
                    .includes(search.toLocaleLowerCase())
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    function sortArray(object1: any, object2: any) {
        if (selectedItems.includes(object1.id) === selectedItems.includes(object2.id)) {
            if (object1[primaryValue] > object2[primaryValue]) {
                return 1;
            } else if (object1[primaryValue] < object2[primaryValue]) {
                return -1;
            } else {
                return 0;
            }
        } else if (selectedItems.includes(object1.id) && !selectedItems.includes(object2.id)) {
            return -1;
        } else if (!selectedItems.includes(object1.id) && selectedItems.includes(object2.id)) {
            return 1;
        } else {
            return 0;
        }
    }

    const onSelectItem = (object: any) => {
        const selected = selectedItems.includes(object.id);
        if (selected) {
            setSelectedItems(selectedItems.filter((id: any) => id !== object.id));
        } else {
            setSelectedItems([...selectedItems, object.id]);
        }
    };

    const onSelectAllItems = () => {
        if (selectedItems.length !== data.length) {
            setSelectedItems(
                data.map(object => {
                    return object.id;
                })
            );
        } else {
            setSelectedItems([]);
        }
    };

    const renderSelectAllOption = () => {
        const listItemId = `select-all`;

        return (
            <ListItem
                key='listItemSelectAll'
                className={styles.listItem}
                secondaryAction={
                    <Checkbox
                        key='checkboxSelectAll'
                        className={styles.checkbox}
                        edge='end'
                        onChange={onSelectAllItems}
                        inputProps={{ 'aria-labelledby': listItemId }}
                        checked={selectedItems.length === data.length}
                    />
                }
                disablePadding
            >
                <ListItemButton key='listItemButtonSelectAll'>
                    <ListItemText
                        key='listItemTextSelectAll'
                        id={listItemId}
                        className={styles.text}
                        primary={'Selecionar todos'}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    const renderLoadMoreOption = () => {
        return (
            <ListItem key='listItemLoadMore' className={styles.listItem} disablePadding>
                <ListItemButton
                    key='listItemButtonLoadMore'
                    onClick={() => {
                        setMaxRender(maxRender + 20);
                    }}
                >
                    <ListItemText
                        key='listItemTextLoadMore'
                        className={styles.text}
                        primary={'Carregar mais ...'}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    const renderItem = (object: any) => {
        if (itemsRendered < maxRender) {
            itemsRendered++;

            return (
                <ListItem
                    key={`listItem-${object.id}`}
                    className={styles.listItem}
                    secondaryAction={
                        <Checkbox
                            key={`checkbox-${object.id}`}
                            className={styles.checkbox}
                            edge='end'
                            onChange={() => onSelectItem(object)}
                            inputProps={{ 'aria-labelledby': object.id }}
                            checked={selectedItems.includes(object.id)}
                        />
                    }
                    disablePadding
                >
                    <ListItemButton key={`listItemButton-${object.id}`}>
                        <ListItemAvatar
                            key={`listItemAvatar-${object.id}`}
                            className={styles.avatarItem}
                        >
                            <Avatar
                                key={`avatar-${object.id}`}
                                alt={object[primaryValue]}
                                src={object.image ? object.image.url : undefined}
                            />
                        </ListItemAvatar>
                        <ListItemText
                            key={`listItemText-${object.id}`}
                            id={object.id}
                            className={styles.text}
                            primary={object[primaryValue]}
                            secondary={object[secondaryValue ?? '']}
                        />
                    </ListItemButton>
                </ListItem>
            );
        } else if (itemsRendered === maxRender) {
            itemsRendered++;
            return renderLoadMoreOption();
        } else {
            return <></>;
        }
    };

    return (
        <>
            <TextField
                size={windowSize.width <= 800 ? 'small' : 'medium'}
                color='success'
                id='userSearch'
                className={styles.text}
                label='Pesquisar'
                type='search'
                variant='filled'
                value={search}
                onChange={event => {
                    setSearch(event.target.value);

                    if (maxRender > 20) {
                        setMaxRender(20);
                    }
                }}
            />
            <div className={styles.usersList}>
                {filteredItems.length > 0 ? (
                    <>
                        {search.length === 0 && renderSelectAllOption()}
                        {filteredItems.map(renderItem)}
                    </>
                ) : (
                    <ListItem key='listItemUserNotFound' className={styles.listItem} disablePadding>
                        <ListItemButton key='listItemButtonUserNotFound'>
                            <ListItemText
                                key='listItemTextUserNotFound'
                                className={styles.text}
                                primary={'Ups, nenhum usuário foi encontrado!'}
                            />
                        </ListItemButton>
                    </ListItem>
                )}
            </div>
        </>
    );
};
